import React, { useEffect, useRef, useState } from "react";
import { useErrorHandler } from "contexts/errorContext";
import { useBreakpoint } from "hooks/useBreakpoint";
import { postAttempt, getLink } from "api/offers";
import { Goal } from "interfaces/Goal";
import { Promotion } from "interfaces/Promotion";
import { OfferCurrencySale } from "interfaces/OfferCurrencySale";
import { getCachedConfiguration } from "utils/functions/configurationUtil";
import { transformCurrency } from "utils/functions/currencyModUtil";
import { getOfferSaleMultiplierLabel } from "utils/functions/offerSaleUtil";
import OfferModalDescriptionTask from "./OfferModalDescriptionTask";
import OfferModalGoal from "./OfferModalGoal";
import OfferInfoBar from "./OfferInfoBar";
import BoosterModal from "components/ui/BoosterModal";
import SafeHtml from "components/ui/SafeHtml";
import { TiDeviceDesktop, TiVendorAndroid } from "react-icons/ti";
import { TiVendorApple } from "react-icons/ti";
import { IoIosArrowRoundDown } from "react-icons/io";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { MdInfoOutline } from "react-icons/md";

import * as c from "./HeroOfferModalContent.module.css";

interface OfferModalContentProps {
  modalContent: {
    standardGoals?: Goal[];
    totalStandardGoalRewardIac?: number;
    purchaseGoals?: Goal[];
    totalPurchaseGoalRewardIac?: number;
    promotion?: Promotion;
    campaignCurrencySale?: OfferCurrencySale;
    instructions?: string[];
    description: string[];
    requirements?: string[];
    platforms: string[];
    operatingSystems?: string[];
    rewardIac: number;
    title: string;
    thumbnailUrl: string;
    heroImageUrl?: string;
    offerId: number;
    isFeatured: boolean;
  };
  onOpenQrModal: (qrCode: string) => void;
  onOfferAttempt?: () => void;
  onScrollTreshold: (value: boolean) => void;
}

type Platform = "Android" | "iOS" | "Windows";

const platformIcons: Record<Platform, React.JSX.Element> = {
  Android: <TiVendorAndroid color="#A6D864" />,
  iOS: <TiVendorApple />,
  Windows: <TiDeviceDesktop size={19} color="#00a5ed" />,
};

const HeroOfferModalContent = ({
  modalContent,
  onOpenQrModal,
  onOfferAttempt,
  onScrollTreshold,
}: OfferModalContentProps) => {
  const handleError = useErrorHandler();
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const {
    standardGoals,
    totalStandardGoalRewardIac,
    purchaseGoals,
    totalPurchaseGoalRewardIac,
    promotion,
    campaignCurrencySale,
    instructions,
    description,
    requirements,
    platforms,
    operatingSystems,
    rewardIac,
    title,
    thumbnailUrl,
    heroImageUrl,
    offerId,
  } = modalContent;
  const configuration = getCachedConfiguration();
  const currency = transformCurrency(configuration!.currency_name!, rewardIac);
  const originalCurrency =
    promotion == null
      ? undefined
      : transformCurrency(configuration!.currency_name!, promotion?.originalRewardIac!);

  const totalStandardGoalCurrency = transformCurrency(
    configuration!.currency_name!,
    totalStandardGoalRewardIac!,
  );

  const totalOriginalStandardGoalCurrency =
    promotion == null
      ? undefined
      : transformCurrency(
          configuration!.currency_name!,
          promotion?.totalOriginalStandardGoalRewardIac!,
        );

  const totalPurchaseGoalCurrency = transformCurrency(
    configuration!.currency_name!,
    totalPurchaseGoalRewardIac!,
  );

  const totalOriginalPurchaseGoalCurrency =
    promotion == null
      ? undefined
      : transformCurrency(
          configuration!.currency_name!,
          promotion?.totalOriginalPurchaseGoalRewardIac!,
        );

  const totalGoalsCurrency = transformCurrency(
    configuration!.currency_name!,
    totalStandardGoalRewardIac! + totalPurchaseGoalRewardIac!,
  );
  const [url, setUrl] = useState("");
  const [qrCode, setQrCode] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isButtonVisible, setIsButtonVisible] = useState(true);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const SCROLL_THRESHOLD = 300;

  const [openAccordions, setOpenAccordions] = useState<{ [key: string]: boolean }>({
    instructions: true,
    standardGoals: true,
    purchaseGoals: true,
  });

  const isPromotion = () => {
    return promotion !== null && promotion?.originalRewardIac !== rewardIac;
  };

  const toggleAccordion = (key: string) => {
    setOpenAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const basicRequirements = [
    "Must be a new user",
    "Must be completed within specified time window as detailed above",
    "Cannot be connected to a VPN, iCloud Private Relay (iOS users), or use Private Browsing (e.g. Incognito Mode)",
    "You must “Allow” the app/game to track your activity to earn rewards",
    "Active internet connection required to complete offers",
  ];

  useEffect(() => {
    const updateViewportHeight = () => {
      const viewportHeight = window.innerHeight;
      document.body.style.setProperty("--viewportHeight", `${viewportHeight}px`);
    };

    window.addEventListener("resize", updateViewportHeight);
    updateViewportHeight();

    return () => window.removeEventListener("resize", updateViewportHeight);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getLink(offerId, configuration!.hashed_publisher_app_uid!)
      .then((data) => {
        setUrl(data.link);
        setQrCode(data.qrCode);
        setIsLoading(false);
      })
      .catch((error) => {
        handleError(error);
      });
  }, []);

  useEffect(() => {
    const canShowButton = standardGoals!.length > 3 || purchaseGoals!.length > 3;
    if (!canShowButton) setIsButtonVisible(false);

    const modal = modalContentRef.current;

    if (modal) {
      modal.addEventListener("scroll", handleModalScroll);
    }
    return () => {
      if (modal) {
        modal.removeEventListener("scroll", handleModalScroll);
      }
    };
  }, []);

  const handleSendAttempt = () => {
    postAttempt(offerId, configuration!.hashed_publisher_app_uid!)
      .then(() => {
        onOfferAttempt?.();
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleModalScroll = () => {
    const modal = modalContentRef.current;
    if (modal) {
      const scrollPosition = modal.scrollTop;

      if (heroImageUrl) {
        scrollPosition >= BUTTON_THRESHOLD ? onScrollTreshold(true) : onScrollTreshold(false);
      }

      const maxScrollHeight = modal.scrollHeight - modal.clientHeight;
      const thresholdPosition = maxScrollHeight - SCROLL_THRESHOLD;

      if (scrollPosition < thresholdPosition) {
        const canShowButton = standardGoals!.length > 3 || purchaseGoals!.length > 3;
        if (canShowButton) {
          setIsButtonVisible(true);
        } else {
          setIsButtonVisible(false);
        }
      } else {
        setIsButtonVisible(false);
      }
    }
  };

  const scrollModalContent = () => {
    const modal = modalContentRef.current;
    if (modal) {
      modal.scrollBy({ top: 400, behavior: "smooth" });
    }
  };

  const detectMobileOS = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) return "iOS";
    if (/android/i.test(userAgent)) return "Android";
    return "";
  };
  const BUTTON_THRESHOLD = detectMobileOS() === "" ? 280 : 180;

  const mobileOS = detectMobileOS();

  const isWebAvailable =
    operatingSystems?.includes("Windows") || operatingSystems?.includes("MacOS");

  return (
    <div className={c["modal-content"]}>
      <div
        className={`${c["modal-content-container"]} ${detectMobileOS() != "" && c["no-web-style"]}`}
        ref={modalContentRef}
      >
        {heroImageUrl && (
          <div
            className={`${c["hero-image-wrapper"]} ${detectMobileOS() != "" && c["no-web-style"]}`}
          >
            <img src={heroImageUrl} alt="Hero Image" />
          </div>
        )}
        <div className={c["modal-content-task-wrapper"]}>
          <div className={c["booster-modal-wrapper"]}>
            <BoosterModal campaignCurrencySale={campaignCurrencySale} promotion={promotion} />
          </div>
          <div className={c.header}>
            <div className={c["header-left"]}>
              <img src={thumbnailUrl} alt="Game Icon" className={c.icon} />
              <div className={c["header-text"]}>
                <span className={c["title"]}>{title}</span>
                <span className={c["sub-title"]}>
                  Earn up to{" "}
                  {isPromotion() ? (
                    <>
                      <span className={c["line-through"]}>{originalCurrency}&nbsp;</span>
                      <b>{currency}</b>
                    </>
                  ) : (
                    currency
                  )}{" "}
                  with this offer
                </span>
                <div className={c["platform-icons"]}>
                  {operatingSystems !== null &&
                    operatingSystems?.length !== 0 &&
                    operatingSystems?.map((platform) => (
                      <span key={platform}>{platformIcons[platform as Platform] || null}</span>
                    ))}
                </div>
              </div>
            </div>
            <div className={c["header-right"]}>
              {mobileOS === "" && (
                <span className={c["first-time-user"]}>
                  <MdInfoOutline size={"17px"} />
                  Available to first time users only
                </span>
              )}
            </div>
          </div>
          <OfferModalDescriptionTask description={description} isMobileOs={mobileOS} />
          {mobileOS !== "" && (
            <span
              className={`${c["first-time-user-mobile"]} ${
                instructions?.length !== 0 && c["no-margin"]
              }`}
            >
              <MdInfoOutline size={"17px"} />
              Available to first time users only
            </span>
          )}

          {/* Instructions Accordion */}
          {instructions && instructions.length > 0 && (
            <div className={`${c.accordion} ${c["instruction-accordion"]} `}>
              <button
                className={c["instruction-button"]}
                onClick={() => toggleAccordion("instructions")}
              >
                <span>Instructions</span>{" "}
                {openAccordions.instructions ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </button>
              {openAccordions.instructions && (
                <div className={c["accordion-content"]}>
                  {
                    <div className={c["instructions-list-wrapper"]}>
                      {instructions.map((instruction, index) => (
                        <div className={c["html-list-item"]} key={`${instruction}-${index}`}>
                          <span>{`${index + 1}.`}</span>
                          <SafeHtml html={instruction} />
                        </div>
                      ))}
                    </div>
                  }
                </div>
              )}
            </div>
          )}

          {/* Standard Goals Accordion */}
          {standardGoals && standardGoals.length > 0 && (
            <div
              className={`${c.accordion} ${c["standard-goal-accordion"]} ${
                openAccordions.standardGoals ? c["open-accordion"] : ""
              } ${purchaseGoals?.length === 0 && c["no-purchase-accordion"]}`}
            >
              <button
                className={`${c["standard-goal-button"]} ${isPromotion() && c["mobile-promotion"]}`}
                onClick={() => toggleAccordion("standardGoals")}
              >
                {purchaseGoals && purchaseGoals.length === 0 ? "Goals" : "Standard Goals"}{" "}
                <div className={c["reward-wrapper"]}>
                  {isPromotion() && (
                    <span className={c["original-reward"]}>
                      {totalOriginalStandardGoalCurrency}
                    </span>
                  )}
                  <span className={c["currency"]}>{totalStandardGoalCurrency}</span>
                  {isPromotion() && (
                    <span className={c["boost-label"]}>
                      {getOfferSaleMultiplierLabel(promotion?.multiplier!)}
                    </span>
                  )}
                </div>
                {openAccordions.standardGoals ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </button>
              {openAccordions.standardGoals && (
                <div className={c["accordion-content"]}>
                  {standardGoals.map((goal, index) => (
                    <React.Fragment key={`${goal.id}-${index}`}>
                      <OfferModalGoal
                        key={goal.id}
                        goal={goal}
                        currencyName={configuration!.currency_name!}
                        index={index}
                      />
                      {index === 0 && goal.title.toLowerCase().includes("install") && (
                        <OfferInfoBar
                          requirements={requirements}
                          goal={goal}
                          configuration={configuration!}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Purchase Goals Accordion */}
          {purchaseGoals && purchaseGoals.length > 0 && (
            <div
              className={`${c.accordion} ${c["purchase-goal-accordion"]} ${
                openAccordions.purchaseGoals ? c["open-accordion"] : ""
              }`}
            >
              <button
                className={`${c["purchase-goal-button"]} ${isPromotion() && c["mobile-promotion"]}`}
                onClick={() => toggleAccordion("purchaseGoals")}
              >
                {standardGoals && standardGoals.length === 0 ? "Goals" : "Purchase Goals"}{" "}
                <div className={c["reward-wrapper"]}>
                  {isPromotion() && (
                    <span className={c["original-reward"]}>
                      {totalOriginalPurchaseGoalCurrency}
                    </span>
                  )}
                  <span className={c["currency"]}>{totalPurchaseGoalCurrency}</span>
                  {isPromotion() && (
                    <span className={c["boost-label"]}>
                      {getOfferSaleMultiplierLabel(promotion?.multiplier!)}
                    </span>
                  )}
                </div>
                {openAccordions.purchaseGoals ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </button>
              {openAccordions.purchaseGoals && (
                <div className={c["accordion-content"]}>
                  <div className={c["purchase-info-wrapper"]}>
                    <span className={c["purchase-text-bold"]}>Unlock goals up to 3× faster!</span>
                    <span>
                      Our highest earners often use in-app purchases to complete more goals in less
                      time.
                    </span>
                  </div>
                  {purchaseGoals.map((goal, index) => (
                    <OfferModalGoal
                      key={goal.id}
                      goal={goal}
                      currencyName={configuration!.currency_name!}
                      index={index}
                    />
                  ))}
                </div>
              )}
              <div className={c["total-rewards"]}>
                <span className={c["total-rewards-text-bold"]}>Total rewards</span>
                <span>{totalGoalsCurrency}</span>
              </div>
            </div>
          )}
          {
            <div className={c["basic-requirements"]}>
              <span>Basic Requirements</span>
              <div className={c["basic-requirements-list-wrapper"]}>
                {(requirements && requirements.length > 0 ? requirements : basicRequirements).map(
                  (req, index) => (
                    <div className={c["html-list-item"]} key={index}>
                      <span>{`${index + 1}.`}</span>
                      <SafeHtml html={req} />
                    </div>
                  ),
                )}
              </div>
            </div>
          }
        </div>
      </div>
      <div className={c["button-wrapper"]}>
        {isButtonVisible && (
          <div className={c["scroll-down-button-wrapper"]}>
            <button className={c["scroll-down-button"]} onClick={scrollModalContent}>
              <IoIosArrowRoundDown size={"18px"} style={{ marginRight: "2px" }} /> More goals
            </button>
          </div>
        )}

        {!isWebAvailable && !isMobile && (
          <a
            onClick={(e) => {
              e.preventDefault();
              handleSendAttempt();
              onOpenQrModal(qrCode);
            }}
            className={`${c["sign-up-button"]} ${!isMobile && isLoading && c["disabled"]}`}
          >
            Get started and earn up to <span className={c.points}>&nbsp;{currency}</span>
          </a>
        )}

        {(isMobile || isWebAvailable) && (
          <a
            href={url}
            onClick={(e) => {
              handleSendAttempt();
            }}
            target="_blank"
            className={`${c["sign-up-button"]} ${!isMobile && isLoading && c["disabled"]}`}
          >
            Get started and earn up to <span className={c.points}>&nbsp;{currency}</span>
          </a>
        )}
      </div>
    </div>
  );
};

export default HeroOfferModalContent;
